import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { AppNotificationUtilClass, Plani8DataService } from '@monorepo/services';
import * as TTnaApprovalsActions from './tna-approvals.actions';
import { TTnaApprovalsActionType } from './tna-approvals.models';
import { switchMap, map, catchError, finalize, take } from 'rxjs/operators';
import { from, of } from 'rxjs';
import { TNA_APPROVALS_ITEMS_PER_PAGE_LIST } from './tna-approvals.facade';

@Injectable({ providedIn: 'root' })
export class TnaApprovalsEffects {
  constructor(
    private actions$: Actions,
    private plani8DataService: Plani8DataService,
    private appNotificationUtilClass: AppNotificationUtilClass,
  ) {}

  fetchTnaStepApprovalList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaApprovalsActionType.GetTnaStepApprovalList),
      switchMap((action: TTnaApprovalsActions.GetTnaStepApprovalList) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),
      switchMap(([loader, action]) =>
        this.plani8DataService.fetchTnaStepApprovalList(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loader);
          }),
          map((res) => new TTnaApprovalsActions.GetTnaStepApprovalListSuccess(res, action.fetchReason)),
          catchError(() => {
            return of(new TTnaApprovalsActions.GetTnaStepApprovalListFailure());
          }),
        ),
      ),
    ),
  );

  fetchTnaStepApprovalDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaApprovalsActionType.GetTnaStepApprovalDetails),
      switchMap((action: TTnaApprovalsActions.GetTnaStepApprovalDetails) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),
      switchMap(([loader, action]) =>
        this.plani8DataService.fetchTnaStepApprovalDetails(action.tosId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loader);
          }),
          map((res) => {
            return new TTnaApprovalsActions.GetTnaStepApprovalDetailsSuccess(res, action.tosId);
          }),
          catchError(() => {
            return of(new TTnaApprovalsActions.GetTnaStepApprovalDetailsFailure());
          }),
        ),
      ),
    ),
  );

  fetchTnaStepApprovalPendingList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaApprovalsActionType.GetTnaStepApprovalPendingList),
      switchMap((action: TTnaApprovalsActions.GetTnaStepApprovalPendingList) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),
      switchMap(([loader, action]) =>
        this.plani8DataService.fetchTnaStepApprovalPendingList(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loader);
          }),
          map((res) => new TTnaApprovalsActions.GetTnaStepApprovalPendingListSuccess(res, action.fetchReason)),
          catchError(() => {
            return of(new TTnaApprovalsActions.GetTnaStepApprovalPendingListFailure());
          }),
        ),
      ),
    ),
  );

  fetchTnaStepApprovalPendingDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaApprovalsActionType.GetTnaStepApprovalPendingDetails),
      switchMap((action: TTnaApprovalsActions.GetTnaStepApprovalPendingDetails) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),
      switchMap(([loader, action]) =>
        this.plani8DataService.fetchTnaStepApprovalPendingDetails(action.approvalId).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loader);
          }),
          map((res) => {
            return new TTnaApprovalsActions.GetTnaStepApprovalPendingDetailsSuccess(res, action.approvalId);
          }),
          catchError(() => {
            return of(new TTnaApprovalsActions.GetTnaStepApprovalPendingDetailsFailure());
          }),
        ),
      ),
    ),
  );

  requestTnaStepApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaApprovalsActionType.TnaStepApprovalRequest),
      switchMap((action: TTnaApprovalsActions.TnaStepApprovalRequest) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),
      switchMap(([loader, action]) =>
        this.plani8DataService.requestTnaStepApproval(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loader);
          }),
          map(() => {
            return new TTnaApprovalsActions.GetTnaStepApprovalList(action.listingPayload, 'STEP_ACTION_UPDATE');
          }),
          catchError(() => {
            return of(new TTnaApprovalsActions.TnaStepApprovalRequestFailure());
          }),
        ),
      ),
    ),
  );

  updateTnaStepApproval$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TTnaApprovalsActionType.UpdateTnaStepApproval),
      switchMap((action: TTnaApprovalsActions.UpdateTnaStepApproval) =>
        from(this.appNotificationUtilClass.presentLoading()).pipe(
          take(1),
          map((loader) => {
            return [loader, action] as const;
          }),
        ),
      ),
      switchMap(([loader, action]) =>
        this.plani8DataService.updateTnaStepApproval(action.payload).pipe(
          finalize(() => {
            this.appNotificationUtilClass.dismissLoadingV2(loader);
          }),
          map(() => {
            return new TTnaApprovalsActions.GetTnaStepApprovalPendingList(action.listingPayload, 'STEP_ACTION_UPDATE');
          }),
          catchError(() => {
            return of(new TTnaApprovalsActions.UpdateTnaStepApprovalFailure());
          }),
        ),
      ),
    ),
  );
}
